import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatPrice } from '@klareospace/pharos-lib-front-utils';
import type { Item } from '@klareospace/pharos-lib-types';
import type { DataTableColumn } from '@klareospace/pharos-lib-web-utils';
import { CodeCell, DataTable, MeasurementCell, TextCell } from '@klareospace/pharos-lib-web-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ItemsTableProps {
  readonly items: Item[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ItemsTable({ items }: ItemsTableProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { i18n, t } = useTranslation('orders');

  const columns = useMemo<DataTableColumn<Item>[]>(
    () => [
      {
        cell: (item): React.ReactNode => <span>{item.label}</span>,
        header: t('drawer.content.columns.label'),
        key: 'label',
      },
      {
        cell: (item): React.ReactNode => <TextCell className="text-foreground">{item.type ?? '—'}</TextCell>,
        header: t('drawer.content.columns.type'),
        key: 'type',
      },
      {
        cell: (item): React.ReactNode => (
          <MeasurementCell
            className="text-foreground"
            unit={item.weight ? tCommon(`units.${item.weight.unit}`) : undefined}
            value={item.weight?.value}
          />
        ),
        header: t('drawer.content.columns.weight'),
        key: 'weight',
      },
      {
        cell: (item): React.ReactNode => (
          <MeasurementCell
            className="text-foreground"
            unit={item.volume ? tCommon(`units.${item.volume.unit}`) : undefined}
            value={item.volume?.value}
          />
        ),
        header: t('drawer.content.columns.volume'),
        key: 'volume',
      },
      {
        cell: (item): React.ReactNode => formatPrice(item.price, i18n.language),
        header: t('drawer.content.columns.price'),
        key: 'price',
      },
      {
        cell: (item): React.ReactNode => (
          <CodeCell className="text-sm text-foreground">{item.code?.value ?? '—'}</CodeCell>
        ),
        header: t('drawer.content.columns.ean'),
        key: 'ean',
      },
      {
        cell: (item): React.ReactNode => <span className="font-medium">{item.quantity}</span>,
        cellClassName: 'text-end',
        className: 'text-end',
        header: t('drawer.content.columns.qty'),
        key: 'qty',
      },
    ],
    [i18n.language, t, tCommon],
  );

  return (
    <DataTable<Item>
      ariaLabel={t('drawer.content.itemsAriaLabel')}
      columns={columns}
      emptyMessage={t('drawer.content.noItems')}
      rows={items}
    />
  );
}
